import React, { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import confetti from 'canvas-confetti'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { Briefcase, Users, Building2, Clock, CheckCircle, AlertCircle, Plus, TrendingUp, Calendar, Package, Star, Cake, Pin, CalendarDays, Gift, Flame, Trophy, Sun, Moon, XCircle, PartyPopper, Heart, BarChart2, X, MessageSquare, Trash2, Bell, ChevronDown } from 'lucide-react'
import api from '../api/axios'
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts'

function useCountUp(target: number, duration: number = 2500) {
  const [count, setCount] = React.useState(0)
  React.useEffect(() => {
    if (target === 0) { setCount(0); return }
    let start = 0
    const step = Math.ceil(target / (duration / 16))
    const timer = setInterval(() => {
      start += step
      if (start >= target) { setCount(target); clearInterval(timer) }
      else setCount(start)
    }, 16)
    return () => clearInterval(timer)
  }, [target])
  return count
}

function Sparkline({ data, color }: { data: number[], color: string }) {
  const max = Math.max(...data, 1)
  const w = 60, h = 24
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w
    const y = h - (v / max) * h
    return `${x},${y}`
  }).join(' ')
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="mt-1">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.7"
      />
    </svg>
  )
}

function CountUpInline({ value }: { value: number }) {
  const count = useCountUp(value)
  return <>{count}</>
}

function ClientDashboard({ user }: { user: any }) {
  const navigate = useNavigate()
  const [client, setClient] = useState<any>(null)
  const [stats, setStats] = useState<any>(null)
  const [recentJobs, setRecentJobs] = useState<any[]>([])
  const [history, setHistory] = useState<any[]>([])
  const [historyPage, setHistoryPage] = useState(1)
  const [historyTotal, setHistoryTotal] = useState(0)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [loading, setLoading] = useState(true)
  const [feedbackStats, setFeedbackStats] = useState<any>(null)


  const fetchData = async () => {
    setLoading(true)
    try {
      const [clientRes, jobsRes, recentRes] = await Promise.all([
        api.get('/clients/me'),
        api.get('/jobs?limit=100'),
        api.get('/jobs?limit=5&page=1')
      ])
      setRecentJobs(recentRes.data.jobs || [])
      const clientData = clientRes.data
      const jobs = jobsRes.data.jobs || []

      // Current month range
      const now = new Date()
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
      const sub = clientData.activeSubscription

      // For subscription clients: jobs in current subscription period
      const periodStart = sub ? new Date(sub.startDate) : monthStart
      const periodJobs = jobs.filter((j: any) => new Date(j.createdAt) >= periodStart)

      const freshCount = periodJobs.filter((j: any) => j.requestType === 'fresh').length
      const revisionCount = periodJobs.filter((j: any) => j.requestType === 'revision').length
      const inProgressCount = jobs.filter((j: any) => j.status !== 'Completed').length
      const completedCount = periodJobs.filter((j: any) => j.status === 'Completed').length
      const monthJobs = jobs.filter((j: any) => new Date(j.createdAt) >= monthStart)

      // Fetch feedback stats
      try {
        const fbRes = await api.get('/jobs/feedback/all')
        const fb = fbRes.data || []
        if (fb.length > 0) {
          setFeedbackStats({
            count: fb.length,
            avg: (fb.reduce((s: number, f: any) => s + f.rating, 0) / fb.length).toFixed(1)
          })
        }
      } catch {}

      setClient(clientData)
      setStats({
        sub,
        inProgress: inProgressCount,
        completed: completedCount,
        fresh: freshCount,
        revision: revisionCount,
        monthTotal: monthJobs.length,
        monthCompleted: monthJobs.filter((j: any) => j.status === 'Completed').length,
        remainingJobs: sub ? sub.totalJobsAllowed - sub.jobsUsed : null,
        daysLeft: sub ? Math.max(0, Math.ceil((new Date(sub.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : null,
      })
    } catch {}
    setLoading(false)
  }

  const fetchHistory = async () => {
    try {
      let url = `/jobs?limit=20&page=${historyPage}&status=Completed`
      if (dateFrom) url += `&dateFrom=${dateFrom}`
      if (dateTo) url += `&dateTo=${dateTo}`
      const res = await api.get(url)
      setHistory(res.data.jobs || [])
      setHistoryTotal(res.data.pagination?.total || 0)
    } catch {}
  }

  useEffect(() => {
    fetchData()
    api.post('/clients/activity', { eventType: 'login', metadata: { page: 'dashboard' } }).catch(() => {})
  }, [])
  useEffect(() => { fetchHistory() }, [historyPage, dateFrom, dateTo])

  if (loading) return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto space-y-4">
      <div className="skeleton h-8 w-48 mb-6" />
      <div className="grid grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="card p-4 space-y-2">
            <div className="skeleton h-8 w-16" />
            <div className="skeleton h-4 w-24" />
          </div>
        ))}
      </div>
      <div className="card p-4 space-y-3">
        <div className="skeleton h-4 w-32" />
        <div className="skeleton h-3 w-full" />
        <div className="skeleton h-3 w-3/4" />
      </div>
    </div>
  )

  const sub = stats?.sub

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-400 font-medium mb-1">{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
          <h1 className="font-display text-3xl font-bold text-navy-900 dark:text-white">Welcome back, {user?.name?.split(' ')[0]}</h1>
          <p className="text-gray-400 text-sm mt-1">{client?.companyName}</p>
        </div>
        <button onClick={() => navigate('/jobs')}
          className="btn-primary flex items-center gap-2 flex-shrink-0">
          <Plus size={15} /> New Job
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="card p-5 transition-all duration-200 hover:shadow-[0_4px_16px_rgba(249,115,22,0.15)] hover:border-orange-200 group">
          <div className="flex items-baseline gap-2">
            <p className="text-4xl font-display font-bold text-[#123D68] dark:text-white leading-none group-hover:text-orange-500 transition-colors">{stats?.inProgress}</p>
            <p className="text-xs font-light text-gray-400 leading-none">In Progress</p>
          </div>
        </div>
        <div className="card p-5 transition-all duration-200 hover:shadow-[0_4px_16px_rgba(249,115,22,0.15)] hover:border-orange-200 group">
          <div className="flex items-baseline gap-2">
            <p className="text-4xl font-display font-bold text-[#123D68] dark:text-white leading-none group-hover:text-orange-500 transition-colors">{stats?.completed}</p>
            <p className="text-xs font-light text-gray-400 leading-none">Completed</p>
          </div>
        </div>
        <div className="card p-5 transition-all duration-200 hover:shadow-[0_4px_16px_rgba(249,115,22,0.15)] hover:border-orange-200 group">
          <div className="flex items-baseline gap-2">
            <p className="text-4xl font-display font-bold text-[#123D68] dark:text-white leading-none group-hover:text-orange-500 transition-colors">{stats?.fresh}</p>
            <p className="text-xs font-light text-gray-400 leading-none">Fresh Jobs</p>
          </div>
        </div>
        <div className="card p-5 transition-all duration-200 hover:shadow-[0_4px_16px_rgba(249,115,22,0.15)] hover:border-orange-200 group">
          <div className="flex items-baseline gap-2">
            <p className="text-4xl font-display font-bold text-[#123D68] dark:text-white leading-none group-hover:text-orange-500 transition-colors">{stats?.revision}</p>
            <p className="text-xs font-light text-gray-400 leading-none">Revisions</p>
          </div>
        </div>
      </div>

      {/* Subscription ring chart + info */}
      {sub && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-orange-500 text-xs font-semibold uppercase tracking-wide">Active Subscription</p>
              <p className="text-sm text-gray-400 mt-0.5">{new Date(sub.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} — {new Date(sub.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
            </div>
            <span className={`text-xs font-semibold px-3 py-1 rounded-full ${stats.daysLeft <= 7 ? 'bg-red-50 text-red-500' : stats.daysLeft <= 14 ? 'bg-yellow-50 text-yellow-600' : 'bg-green-50 text-green-600'}`}>
              {new Date(sub.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          </div>
          <div className="flex items-center gap-8">
            {/* Ring charts — Jobs + Days */}
            <div className="flex gap-4 flex-shrink-0">
              {/* Jobs ring */}
              <div className="flex flex-col items-center gap-1">
                <div className="relative">
                  <PieChart width={120} height={120}>
                    <Pie data={[
                      { name: 'Used', value: sub.jobsUsed },
                      { name: 'Remaining', value: Math.max(0, stats.remainingJobs) }
                    ]} cx={55} cy={55} innerRadius={38} outerRadius={55} startAngle={90} endAngle={-270} dataKey="value" strokeWidth={0}>
                      <Cell fill="#f97316" />
                      <Cell fill="#fed7aa" />
                    </Pie>
                  </PieChart>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <p className="text-xl font-bold text-navy-900 dark:text-white">{stats.remainingJobs}</p>
                    <p className="text-[9px] text-gray-400">jobs left</p>
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 font-medium">Jobs</p>
              </div>
              {/* Days ring */}
              <div className="flex flex-col items-center gap-1">
                {(() => {
                  const totalDays = Math.max(1, Math.round((new Date(sub.endDate).getTime() - new Date(sub.startDate).getTime()) / (1000 * 60 * 60 * 24)))
                  const daysUsed = Math.max(0, totalDays - stats.daysLeft)
                  const dayColor = stats.daysLeft <= 7 ? '#ef4444' : stats.daysLeft <= 14 ? '#f59e0b' : '#22c55e'
                  const dayBg = stats.daysLeft <= 7 ? '#fee2e2' : stats.daysLeft <= 14 ? '#fef3c7' : '#dcfce7'
                  return (
                    <div className="relative">
                      <PieChart width={120} height={120}>
                        <Pie data={[
                          { name: 'Used', value: daysUsed },
                          { name: 'Remaining', value: Math.max(0, stats.daysLeft) }
                        ]} cx={55} cy={55} innerRadius={38} outerRadius={55} startAngle={90} endAngle={-270} dataKey="value" strokeWidth={0}>
                          <Cell fill={dayColor} />
                          <Cell fill={dayBg} />
                        </Pie>
                      </PieChart>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <p className="text-xl font-bold text-navy-900 dark:text-white">{stats.daysLeft}</p>
                        <p className="text-[9px] text-gray-400">days left</p>
                      </div>
                    </div>
                  )
                })()}
                <p className="text-[10px] text-gray-400 font-medium">Days</p>
              </div>
            </div>
            {/* Stats */}
            <div className="flex-1 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Jobs Used</span>
                <span className="text-sm font-semibold text-navy-800 dark:text-white">{sub.jobsUsed} / {sub.totalJobsAllowed}</span>
              </div>
              <div className="h-2 bg-orange-100 dark:bg-orange-900/30 rounded-full overflow-hidden">
                <div className="h-full bg-orange-400 rounded-full" style={{ width: `${Math.min((sub.jobsUsed / sub.totalJobsAllowed) * 100, 100)}%` }} />
              </div>
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div className="bg-gray-50 dark:bg-[#112240] rounded-xl p-3">
                  <p className="text-lg font-bold text-green-600">{stats.completed}</p>
                  <p className="text-[10px] text-gray-400">Completed</p>
                </div>
                <div className="bg-gray-50 dark:bg-[#112240] rounded-xl p-3">
                  <p className="text-lg font-bold text-blue-500">{stats.inProgress}</p>
                  <p className="text-[10px] text-gray-400">In Progress</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Jobs + Feedback */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {/* Recent jobs */}
        <div className="card overflow-hidden sm:col-span-2">
          <div className="px-5 py-3.5 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between">
            <h2 className="font-display font-semibold text-navy-900 dark:text-white text-sm">Recent Jobs</h2>
            <button onClick={() => navigate('/jobs')} className="text-xs text-orange-500 hover:text-orange-700">View all →</button>
          </div>
          <div className="divide-y divide-gray-50 dark:divide-[#1a3050]">
            {recentJobs.length === 0 ? (
              <div className="py-8 text-center text-gray-400 text-sm">No jobs yet</div>
            ) : recentJobs.map((job: any) => (
              <div key={job.id} className="px-5 py-3 flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  job.status === 'Completed' ? 'bg-green-400' :
                  job.status === 'In Process' ? 'bg-orange-400' :
                  job.status === 'Send to QC' ? 'bg-purple-400' :
                  'bg-gray-300'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-navy-800 dark:text-white truncate">{job.displayTitle}</p>
                  <p className="text-[10px] text-gray-400 font-mono">{job.jobCode}</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                  job.status === 'Completed' ? 'bg-green-50 text-green-600' :
                  job.status === 'In Process' ? 'bg-orange-50 text-orange-600' :
                  'bg-gray-50 text-gray-500'
                }`}>{job.status === 'Completed' ? 'Done' : job.status === 'In Process' ? 'In Progress' : job.status}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Feedback summary */}
        <div className="card p-5 flex flex-col items-center justify-center text-center">
          {feedbackStats ? (
            <>
              <div className="text-4xl font-bold text-yellow-500 mb-1">{feedbackStats.avg}</div>
              <div className="flex gap-0.5 mb-2">
                {[1,2,3,4,5].map(i => (
                  <span key={i} className={`text-lg ${i <= Math.round(parseFloat(feedbackStats.avg)) ? 'text-yellow-400' : 'text-gray-200'}`}>★</span>
                ))}
              </div>
              <p className="text-xs text-gray-400">Average rating across</p>
              <p className="text-xs text-gray-400">{feedbackStats.count} completed jobs</p>
            </>
          ) : (
            <>
              <div className="mb-2"><Star size={32} className="text-yellow-400 mx-auto" /></div>
              <p className="text-sm font-semibold text-navy-800 dark:text-white mb-1">No feedback yet</p>
              <p className="text-xs text-gray-400">Rate your completed jobs</p>
            </>
          )}
        </div>
      </div>

      {/* Job History */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-display font-semibold text-navy-900">Completed Job History</h2>
          <div className="flex items-center gap-2">
            <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setHistoryPage(1) }}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600" />
            <span className="text-xs text-gray-400">to</span>
            <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setHistoryPage(1) }}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600" />
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(''); setDateTo(''); setHistoryPage(1) }}
                className="text-xs text-red-400 hover:text-red-600">Clear</button>
            )}
          </div>
        </div>
        {!history.length ? (
          <div className="py-12 text-center text-gray-400 text-sm">No completed jobs found</div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-gray-50/70">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Job</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Type</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {history.map((job: any) => (
                  <tr key={job.id} className="hover:bg-green-50/30">
                    <td className="px-6 py-3.5">
                      <p className="font-medium text-navy-800">{job.displayTitle}</p>
                      <p className="text-xs font-mono text-gray-400">{job.jobCode}</p>
                    </td>
                    <td className="px-6 py-3.5">
                      <span className="text-xs capitalize text-gray-500">{job.requestType}</span>
                    </td>
                    <td className="px-6 py-3.5 text-xs text-gray-400">
                      {new Date(job.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Pagination */}
            {historyTotal > 20 && (
              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-400">{historyTotal} total completed jobs</p>
                <div className="flex gap-2">
                  <button disabled={historyPage === 1} onClick={() => setHistoryPage(p => p - 1)}
                    className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Previous</button>
                  <span className="text-xs px-3 py-1.5 text-gray-500">Page {historyPage}</span>
                  <button disabled={historyPage * 20 >= historyTotal} onClick={() => setHistoryPage(p => p + 1)}
                    className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
     </div>
  )
}

function StaffDashboard({ user }: { user: any }) {
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const isAdmin = user.roleName === 'admin'
  const isArtist = hasPermission('can_work_on_jobs') && !hasPermission('exclude_from_leaderboard')
  const canManage = isAdmin || hasPermission('exclude_from_leaderboard')
  const canViewDashboard = isAdmin || hasPermission('view_dashboard')
  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
  const todayStr = new Date().toISOString().split('T')[0]

  const [commonStats, setCommonStats] = useState({ todayJobs: 0, pendingJobs: 0, inProgressJobs: 0 })
  const [myStats, setMyStats] = useState({ myJobs: 0, myInProgress: 0, myCompleted: 0 })
  const [adminStats, setAdminStats] = useState({ activeClients: 0, subscriptionClients: 0, staffMembers: 0 })
  const [todayJobs, setTodayJobs] = useState<any[]>([])
  const [dashLoading, setDashLoading] = useState(true)
  const [clientList, setClientList] = useState<any[]>([])
  const [analytics, setAnalytics] = useState<any>(null)
  const [clientFlow, setClientFlow] = useState<any>(null)
  const [dailyTarget, setDailyTarget] = useState<any>(null)
  const confettiFired = useRef(sessionStorage.getItem('confetti_fired_today') === new Date().toLocaleDateString('en-CA'))
  const [leaderboard, setLeaderboard] = useState<any[]>([])
  const [leaderboardPeriod, setLeaderboardPeriod] = useState<'weekly' | 'monthly'>('weekly')
  const [hasAnyLeaderboardData, setHasAnyLeaderboardData] = useState(false)
  const [myRank, setMyRank] = useState<any>(null)
  const [dashboardLeaderboardEnabled, setDashboardLeaderboardEnabled] = useState(true)
  const [notices, setNotices] = useState<any[]>([])
  const [noticeBoardEnabled, setNoticeBoardEnabled] = useState(true)
  const [showNoticeForm, setShowNoticeForm] = useState(false)
  const [unreadNotices, setUnreadNotices] = useState<any[]>([])
  const [currentUnread, setCurrentUnread] = useState<any>(null)
  const [noticeForm, setNoticeForm] = useState({ title: '', content: '', isPinned: false, expiresAt: '', linkUrl: '', linkLabel: '' })
  const [savingNotice, setSavingNotice] = useState(false)
  const [handoverNotes, setHandoverNotes] = useState<any[]>([])
  const [showHandover, setShowHandover] = useState(true)
  const [showHandoverForm, setShowHandoverForm] = useState(false)
  const [handoverContent, setHandoverContent] = useState('')
  const [handoverPriority, setHandoverPriority] = useState('normal')
  const [handoverJobCode, setHandoverJobCode] = useState('')
  const [handoverJobId, setHandoverJobId] = useState('')
  const [savingHandover, setSavingHandover] = useState(false)

  const fetchHandoverNotes = async () => {
    try {
      const res = await api.get('/handover')
      setHandoverNotes(res.data)
    } catch {}
  }
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showHandover) {
        setShowHandover(false)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [showHandover])
  const [birthdays, setBirthdays] = useState<any[]>([])
  const [birthdayWallEnabled, setBirthdayWallEnabled] = useState(true)
  const [starAward, setStarAward] = useState<any>(null)
  const [starAwardEnabled, setStarAwardEnabled] = useState(true)
  const canViewAnalytics = isAdmin || hasPermission('view_analytics_dashboard')
  const canViewClientFlow = isAdmin || hasPermission('view_client_flow_chart')
  const [clientFlowChart, setClientFlowChart] = useState<'bar' | 'line'>('bar')
  const [jobsChart, setJobsChart] = useState<'bar' | 'line'>('bar')
  const [hoursChart, setHoursChart] = useState<'bar' | 'line'>('bar')

  useEffect(() => {
    if (isArtist) {
      api.get('/performance/daily-target').then(res => {
        setDailyTarget(res.data)
        if (res.data.percentage >= 100 && !confettiFired.current) {
          confettiFired.current = true
          sessionStorage.setItem('confetti_fired_today', new Date().toLocaleDateString('en-CA'))
          setTimeout(() => {
            confetti({
              particleCount: 120,
              spread: 80,
              origin: { y: 0.6 },
              colors: ['#F46B20', '#123d68', '#f97316', '#3b82f6', '#22c55e', '#facc15']
            })
          }, 800)
        }
      }).catch(() => {})
    }
  }, [isArtist])

  useEffect(() => {
    api.get('/settings/public/dashboard_leaderboard_enabled').then(res => setDashboardLeaderboardEnabled(res.data.value !== 'false')).catch(() => {})
    api.get('/settings/public/notice_board_enabled').then(res => setNoticeBoardEnabled(res.data.value !== 'false')).catch(() => {})
    api.get('/notices').then(res => setNotices(res.data)).catch(() => {})
    if (user?.roleName !== 'client') {
      api.get('/notices/unread').then(res => {
        if (res.data.length > 0) {
          setUnreadNotices(res.data)
          setCurrentUnread(res.data[0])
        }
      }).catch(() => {})
    }
    api.get('/settings/public/birthday_wall_enabled').then(res => setBirthdayWallEnabled(res.data.value !== 'false')).catch(() => {})
    api.get('/notices/birthdays').then(res => setBirthdays(res.data)).catch(() => {})
    fetchHandoverNotes()
    api.get('/settings/public/star_award_enabled').then(res => setStarAwardEnabled(res.data.value !== 'false')).catch(() => {})
    api.get('/announcements/star-award/current').then(res => setStarAward(res.data)).catch(() => {})
  }, [])

  useEffect(() => {
    if (isArtist) {
      api.get(`/performance/leaderboard?period=${leaderboardPeriod}`).then(res => {
        const board = res.data.leaderboard || []
        setLeaderboard(board.slice(0, 3))
        if (board.length > 0) setHasAnyLeaderboardData(true)
        const mine = board.find((e: any) => e.artistId === user?.userId?.toString())
        setMyRank(mine || null)
      }).catch(() => {})
    }
  }, [isArtist, leaderboardPeriod])

  // Check if any leaderboard data exists (weekly or monthly) on mount
  useEffect(() => {
    if (!isArtist) return
    Promise.all([
      api.get('/performance/leaderboard?period=weekly').catch(() => ({ data: { leaderboard: [] } })),
      api.get('/performance/leaderboard?period=monthly').catch(() => ({ data: { leaderboard: [] } }))
    ]).then(([weekly, monthly]) => {
      const hasData = (weekly.data.leaderboard?.length > 0) || (monthly.data.leaderboard?.length > 0)
      setHasAnyLeaderboardData(hasData)
      // Default to monthly if weekly is empty
      if (!weekly.data.leaderboard?.length && monthly.data.leaderboard?.length > 0) {
        setLeaderboardPeriod('monthly')
      }
    })
  }, [isArtist])

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const canAccessJobs = hasPermission('access_jobs') || hasPermission('can_work_on_jobs') || isAdmin

        let todayJobs: any[] = []
        let todayTotal = 0

        if (canAccessJobs) {
          const [todayJobsRes, inProgressRes, pendingRes, allocatedRes] = await Promise.all([
            api.get(`/jobs?limit=200&dateFrom=${todayStr}&dateTo=${todayStr}`),
            api.get('/jobs?limit=1&status=In Process'),
            api.get('/jobs?limit=1&status=Unallocated'),
            api.get('/jobs?limit=1&status=Allocated'),
          ])

          todayJobs = todayJobsRes.data.jobs || []
          todayTotal = todayJobsRes.data.pagination?.total || 0

          setCommonStats({
            todayJobs: todayTotal,
            pendingJobs: (pendingRes.data.pagination?.total || 0) + (allocatedRes.data.pagination?.total || 0),
            inProgressJobs: inProgressRes.data.pagination?.total || 0,
          })
        }

        if (!canViewDashboard) {
          // no dashboard data for this role
        } else if (isArtist && !canManage) {
          const statsRes = await api.get('/jobs/dashboard-stats')
          const stats = statsRes.data
          setMyStats({
            myJobs: stats.todaySubmitted,
            myInProgress: stats.activeJobs,
            myCompleted: stats.todayCompleted
          })
          setTodayJobs(stats.jobs || [])

        } else if (canViewDashboard && !hasPermission('access_jobs') && !hasPermission('can_work_on_jobs')) {
          // HR/non-job roles — show staff & attendance summary + analytics if permitted
          try {
            const staffRes = await api.get('/staff')
            setAdminStats({
              activeClients: 0,
              subscriptionClients: 0,
              staffMembers: staffRes.data?.length || 0
            })
          } catch {}
          if (hasPermission('view_analytics_dashboard')) {
            api.get('/jobs/analytics').then(res => setAnalytics(res.data)).catch(() => {})
          }
          if (hasPermission('view_client_flow_chart')) {
            api.get('/jobs/client-flow').then(res => setClientFlow(res.data)).catch(() => {})
          }
        } else if (canManage || canViewDashboard) {
          // Today stats for QC/admin
          const myTodayCompleted = todayJobs.filter((j: any) => j.status === 'Completed').length
          const myTodayInProgress = todayJobs.filter((j: any) => ['Allocated', 'In Process', 'Paused', 'Send to QC'].includes(j.status)).length
          setMyStats({
            myJobs: todayTotal,
            myInProgress: myTodayInProgress,
            myCompleted: myTodayCompleted,
          })

          // Client list — fetch all jobs and group by client
          const clientsRes = await (isAdmin || hasPermission('manage_clients') ? api.get('/clients') : api.get('/clients/basic'))
          const clients: any[] = clientsRes.data || []

          if (isAdmin || hasPermission('manage_clients')) {
            const staffRes = await api.get('/staff/artists')
            // Count clients with active subscription
            const subClients = clients.filter((c: any) => c.activeSubscription && c.activeSubscription.status === 'active').length
            setAdminStats({
              activeClients: clients.filter((c: any) => c.status === 'active').length,
              subscriptionClients: subClients,
              staffMembers: staffRes.data.length || 0,
            })
          }

          // Fetch analytics
          if (isAdmin || hasPermission('view_analytics_dashboard')) {
            api.get('/jobs/analytics').then(res => setAnalytics(res.data)).catch(() => {})
          }
          if (isAdmin || hasPermission('view_client_flow_chart')) {
            api.get('/jobs/client-flow').then(res => setClientFlow(res.data)).catch(() => {})
          }

          // Fetch all jobs for client overview
          const allJobsRes = await api.get('/jobs?limit=500')
          const allJobs = allJobsRes.data.jobs || []

          // Build per-client map using clients list as base
          const clientMap: Record<string, any> = {}
          // Initialize all clients first
          for (const c of clients) {
            clientMap[String(c.clientId)] = {
              name: c.companyName || c.name || 'Unknown',
              abbreviation: c.abbreviation || c.clientCode || '—',
              volume: 0,
              inProgress: 0,
              completed: 0,
            }
          }
          // Count jobs per client
          for (const job of allJobs) {
            // Use client.clientId from nested object OR top-level clientId
            const key = String(job.client?.clientId || job.clientId || 'unknown')
            if (key === 'unknown') continue
            if (!clientMap[key]) {
              // Client not in our list — create from job's client data
              clientMap[key] = {
                name: job.client?.companyName || job.client?.name || 'Unknown',
                abbreviation: job.client?.abbreviation || '—',
                volume: 0, inProgress: 0, completed: 0
              }
            }
            clientMap[key].volume++
            if (['Allocated', 'In Process', 'Paused', 'Send to QC', 'Correction', 'Carryover'].includes(job.status)) clientMap[key].inProgress++
            if (job.status === 'Completed') clientMap[key].completed++
          }
          setClientList(Object.values(clientMap).filter((c: any) => c.volume > 0).sort((a: any, b: any) => b.volume - a.volume))
        }
      } catch (e) { console.error(e) }
      finally { setDashLoading(false) }
    }
    fetchDashboard()
  }, [])

  const statusColors: Record<string, string> = {
    'Unallocated': 'bg-gray-100 text-gray-500',
    'Allocated': 'bg-blue-50 text-blue-600',
    'In Process': 'bg-orange-50 text-orange-600',
    'Paused': 'bg-yellow-50 text-yellow-600',
    'Send to QC': 'bg-purple-50 text-purple-600',
    'Completed': 'bg-green-50 text-green-600',
    'Correction': 'bg-red-50 text-red-600',
    'Carryover': 'bg-indigo-50 text-indigo-600',
  }

  const greeting = new Date().getHours() < 12 ? 'Good morning' : new Date().getHours() < 17 ? 'Good afternoon' : 'Good evening'

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-7">
        <p className="text-xs text-gray-400 font-medium mb-1">{today}</p>
        <h1 className="font-display text-2xl font-bold text-navy-900">{greeting}, {user?.name?.split(' ')[0] || user?.roleLabel}</h1>
        <p className="text-gray-400 text-sm mt-0.5">{user?.roleLabel}</p>
      </div>

      {/* Today's Snapshot Bar */}
      {(canViewDashboard || hasPermission('access_jobs') || hasPermission('can_work_on_jobs')) && (
        <div className="flex flex-wrap gap-2 mb-6">
          <div className="flex items-center gap-2 bg-white dark:bg-[#112240] border border-gray-100 dark:border-[#1e3a5f] rounded-full px-3 py-1.5 shadow-sm">
            <span className="bg-orange-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"><CountUpInline value={commonStats.todayJobs} /></span>
            <span className="text-xs font-medium text-navy-700 dark:text-gray-300">Today</span>
          </div>
          <div className="flex items-center gap-2 bg-white dark:bg-[#112240] border border-gray-100 dark:border-[#1e3a5f] rounded-full px-3 py-1.5 shadow-sm">
            <span className="bg-yellow-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"><CountUpInline value={commonStats.pendingJobs} /></span>
            <span className="text-xs font-medium text-navy-700 dark:text-gray-300">Pending</span>
          </div>
          <div className="flex items-center gap-2 bg-white dark:bg-[#112240] border border-gray-100 dark:border-[#1e3a5f] rounded-full px-3 py-1.5 shadow-sm">
            <span className="bg-blue-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"><CountUpInline value={commonStats.inProgressJobs} /></span>
            <span className="text-xs font-medium text-navy-700 dark:text-gray-300">In Progress</span>
          </div>
          {(hasPermission('access_jobs') || hasPermission('can_work_on_jobs')) && <>
            <div className="flex items-center gap-2 bg-white dark:bg-[#112240] border border-gray-100 dark:border-[#1e3a5f] rounded-full px-3 py-1.5 shadow-sm">
              <span className="bg-green-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"><CountUpInline value={myStats.myCompleted} /></span>
              <span className="text-xs font-medium text-navy-700 dark:text-gray-300">Completed</span>
            </div>
            <div className="flex items-center gap-2 bg-white dark:bg-[#112240] border border-gray-100 dark:border-[#1e3a5f] rounded-full px-3 py-1.5 shadow-sm">
              <span className="bg-purple-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"><CountUpInline value={myStats.myInProgress} /></span>
              <span className="text-xs font-medium text-navy-700 dark:text-gray-300">Active</span>
            </div>
          </>}
        </div>
      )}

      {/* Birthday Banner — for the birthday person */}
      {birthdayWallEnabled && user?.roleName !== 'client' && (() => {
        const myBirthday = birthdays.find(b => b.isToday && b.type === 'birthday' && b.userId === user?.userId?.toString())
        if (!myBirthday) return null
        return (
          <div className="mb-6 card p-5 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border border-yellow-200 dark:border-yellow-500/30">
            <div className="flex items-center gap-4">
              <div><Cake size={36} className="text-pink-400 mx-auto" /></div>
              <div className="flex-1">
                <p className="font-display font-bold text-navy-900 dark:text-white text-lg flex items-center gap-2">Happy Birthday, {user?.name?.split(' ')[0]}! <PartyPopper size={20} className="text-pink-500" /></p>
                <p className="text-sm text-gray-500 dark:text-gray-300 mt-0.5">Wishing you a wonderful day filled with joy!</p>
                {myBirthday.reactions.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(
                        myBirthday.reactions.reduce((acc: any, r: any) => {
                          if (!acc[r.emoji]) acc[r.emoji] = []
                          acc[r.emoji].push(r.reactorName)
                          return acc
                        }, {})
                      ).map(([emoji, names]: any) => (
                        <span key={emoji} title={names.join(', ')}
                          className="text-sm bg-white dark:bg-[#1a3050] border border-yellow-200 dark:border-yellow-500/30 rounded-full px-2.5 py-0.5 cursor-default">
                          {emoji} {names.length}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400">
                      {myBirthday.reactions.map((r: any) => r.reactorName).join(', ')} wished you <Heart size={14} className="inline text-red-400" />
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })()}

      {/* Star Award Banner */}
      {starAwardEnabled && user?.roleName !== 'client' && starAward && (
        <div className="mb-6 card p-5 bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border border-yellow-200 dark:border-yellow-500/30">
          <div className="flex items-center gap-4">
            <div><Star size={36} className="text-yellow-400 mx-auto" /></div>
            <div className="flex-1">
              <p className="text-xs font-semibold text-yellow-600 uppercase tracking-wide mb-0.5">
                {new Date(0, starAward.month - 1).toLocaleString('en-IN', { month: 'long' })} {starAward.year} Star
              </p>
              <p className="font-display font-bold text-navy-900 dark:text-white text-lg flex items-center gap-2">
                {starAward.name} {user?.userId?.toString() === starAward.userId ? <span className="text-sm text-yellow-600 font-medium">(You!)</span> : ''}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-300 mt-0.5">{starAward.role}</p>
              {starAward.reason && <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{starAward.reason}</p>}
              <p className="text-xs text-gray-400 mt-1">Awarded by {starAward.awardedBy}</p>
            </div>
          </div>
        </div>
      )}

      {/* Ink Board + Birthday/Anniversary — side by side on desktop */}
      {user?.roleName !== 'client' && (noticeBoardEnabled || birthdayWallEnabled) && (
        <div className="mb-6 space-y-4">

          {/* Ink Bulletin — 100% */}
          {noticeBoardEnabled && (notices.length > 0 || hasPermission('post_notices')) && (
            <div className={`card overflow-hidden w-full transition-all duration-300 ${notices.some(n => (Date.now() - new Date(n.createdAt).getTime()) < 24 * 60 * 60 * 1000) ? 'ring-1 ring-orange-300 shadow-[0_0_6px_1px_rgba(251,146,60,0.2)]' : ''}`}>
              <div className="px-5 py-3.5 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Pin size={14} className="text-orange-400 flex-shrink-0" />
                  <p className="font-display font-semibold text-navy-900 dark:text-white">Ink Bulletin</p>
                </div>
                {hasPermission('post_notices') && (
                  <button onClick={() => setShowNoticeForm(v => !v)}
                    className="text-xs bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-lg font-medium transition-colors">
                    {showNoticeForm ? 'Cancel' : '+ Add'}
                  </button>
                )}
              </div>

              {showNoticeForm && (
                <div className="px-5 py-4 border-b border-orange-100 dark:border-orange-500/20 bg-orange-50 dark:bg-[#1a2a40] space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="label">Title *</label>
                      <input type="text" className="input text-sm" placeholder="e.g. Server maintenance today"
                        value={noticeForm.title} onChange={e => setNoticeForm(f => ({ ...f, title: e.target.value }))} />
                    </div>
                    <div>
                      <label className="label">Expires On <span className="text-gray-400 font-normal">(optional)</span></label>
                      <input type="date" className="input text-sm"
                        value={noticeForm.expiresAt} onChange={e => setNoticeForm(f => ({ ...f, expiresAt: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <label className="label">Content *</label>
                    <textarea className="input text-sm w-full" rows={2} placeholder="Notice details..."
                      value={noticeForm.content} onChange={e => setNoticeForm(f => ({ ...f, content: e.target.value }))} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="label">Link URL <span className="text-gray-400 font-normal">(optional)</span></label>
                      <input type="url" className="input text-sm" placeholder="https://..."
                        value={noticeForm.linkUrl} onChange={e => setNoticeForm(f => ({ ...f, linkUrl: e.target.value }))} />
                    </div>
                    <div>
                      <label className="label">Link Label <span className="text-gray-400 font-normal">(optional)</span></label>
                      <input type="text" className="input text-sm" placeholder="e.g. View Announcement"
                        value={noticeForm.linkLabel} onChange={e => setNoticeForm(f => ({ ...f, linkLabel: e.target.value }))} />
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={noticeForm.isPinned}
                        onChange={e => setNoticeForm(f => ({ ...f, isPinned: e.target.checked }))}
                        className="accent-orange-500" />
                      <span className="text-sm text-gray-600 dark:text-gray-300">Pin this notice</span>
                    </label>
                    <button onClick={async () => {
                      if (!noticeForm.title.trim() || !noticeForm.content.trim()) return
                      setSavingNotice(true)
                      try {
                        await api.post('/notices', noticeForm)
                        setNoticeForm({ title: '', content: '', isPinned: false, expiresAt: '', linkUrl: '', linkLabel: '' })
                        setShowNoticeForm(false)
                        const res = await api.get('/notices')
                        setNotices(res.data)
                      } catch (e: any) {
                        alert(e.response?.data?.error || 'Failed to post notice')
                      }
                      setSavingNotice(false)
                    }} disabled={savingNotice || !noticeForm.title.trim() || !noticeForm.content.trim()}
                      className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2 rounded-xl transition-colors">
                      {savingNotice ? '⏳ Saving...' : 'Post'}
                    </button>
                  </div>
                </div>
              )}

              {/* Pinned notices */}
              {notices.filter(n => n.isPinned).length === 0 && notices.filter(n => !n.isPinned).length === 0 ? (
                <div className="px-5 py-6 text-center text-gray-400 text-sm">No active notices</div>
              ) : (
                <div className="divide-y divide-gray-50 dark:divide-[#1e3a5f]">
                  {/* Pinned first */}
                  {notices.filter(n => n.isPinned).map(n => (
                    <div key={n.id} className="px-4 py-3 flex items-start gap-2 bg-orange-50/50 dark:bg-orange-900/5">
                      <Pin size={12} className="text-orange-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-semibold text-navy-800 dark:text-white">{n.title}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-300 mt-0.5">{n.content}</p>
                        {n.linkUrl && <button onClick={e => { e.stopPropagation(); const isInternal = n.linkUrl.includes(window.location.hostname) || n.linkUrl.startsWith('/'); isInternal ? navigate(n.linkUrl.replace(window.location.origin, '')) : window.open(n.linkUrl, '_blank') }} className="inline-flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600 font-medium mt-1">{n.linkLabel || 'View details'} →</button>}
                        <p className="text-xs text-gray-400 mt-0.5">{n.createdBy} · {new Date(n.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                      </div>
                      {hasPermission('post_notices') && (
                        <div className="flex gap-1 flex-shrink-0">
                          <button onClick={async () => { await api.patch(`/notices/${n.id}/pin`); const res = await api.get('/notices'); setNotices(res.data) }}
                            className="text-xs text-gray-400 hover:text-orange-500">Unpin</button>
                          <button onClick={async () => { if (!window.confirm('Delete?')) return; await api.delete(`/notices/${n.id}`); setNotices(prev => prev.filter(x => x.id !== n.id)) }}
                            className="text-xs text-red-400 hover:text-red-600">×</button>
                        </div>
                      )}
                    </div>
                  ))}
                  {/* Latest 3 unpinned */}
                  {notices.filter(n => !n.isPinned).slice(0, 3).map(n => (
                    <div key={n.id} className="px-4 py-3 flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-semibold text-navy-800 dark:text-white">{n.title}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-300 mt-0.5">{n.content}</p>
                        {n.linkUrl && <button onClick={e => { e.stopPropagation(); const isInternal = n.linkUrl.includes(window.location.hostname) || n.linkUrl.startsWith('/'); isInternal ? navigate(n.linkUrl.replace(window.location.origin, '')) : window.open(n.linkUrl, '_blank') }} className="inline-flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600 font-medium mt-1">{n.linkLabel || 'View details'} →</button>}
                        <p className="text-xs text-gray-400 mt-0.5">{n.createdBy} · {new Date(n.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          {n.expiresAt && ` · Expires ${new Date(n.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}
                        </p>
                      </div>
                      {hasPermission('post_notices') && (
                        <div className="flex gap-1 flex-shrink-0">
                          <button onClick={async () => { await api.patch(`/notices/${n.id}/pin`); const res = await api.get('/notices'); setNotices(res.data) }}
                            className="text-xs text-gray-400 hover:text-orange-500">Pin</button>
                          <button onClick={async () => { if (!window.confirm('Delete?')) return; await api.delete(`/notices/${n.id}`); setNotices(prev => prev.filter(x => x.id !== n.id)) }}
                            className="text-xs text-red-400 hover:text-red-600">×</button>
                        </div>
                      )}
                    </div>
                  ))}
                  {notices.filter(n => !n.isPinned).length > 3 && (
                    <div className="px-4 py-2 text-center">
                      <p className="text-xs text-gray-400">+{notices.filter(n => !n.isPinned).length - 3} more notices</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Shift Handover Notes — below Ink Bulletin */}
          {user?.roleName !== 'client' && (
            <>
              {showHandover && (handoverNotes.length > 0 || showHandoverForm) && (
                <div className="card overflow-hidden">
                  <div className="px-5 py-3.5 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MessageSquare size={14} className="text-blue-400 flex-shrink-0" />
                      <p className="font-display font-semibold text-navy-900 dark:text-white">Shift Handover</p>
                      <span className="text-[10px] bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300 px-2 py-0.5 rounded-full font-medium">{handoverNotes.length} note{handoverNotes.length > 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setShowHandoverForm(v => !v)}
                        className="text-xs bg-orange-50 dark:bg-orange-900/20 text-orange-500 hover:bg-orange-100 px-3 py-1.5 rounded-lg font-medium transition-colors">
                        + Add Note
                      </button>
                      <button onClick={() => { setShowHandover(false) }}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-[#1e3a5a] transition-colors" title="Close (ESC)">
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                  {showHandoverForm && (
                    <div className="px-5 py-4 border-b border-gray-100 dark:border-[#1e3a5f] bg-blue-50/50 dark:bg-blue-900/5 space-y-3">
                      <textarea className="input resize-none text-sm" rows={2} placeholder="Write handover note..." value={handoverContent} onChange={e => setHandoverContent(e.target.value)} />
                      <div className="flex items-center gap-3">
                        <select className="input text-sm py-1.5 w-32" value={handoverPriority} onChange={e => setHandoverPriority(e.target.value)}>
                          <option value="normal">Normal</option>
                          <option value="urgent">Urgent</option>
                        </select>
                        <input className="input text-sm py-1.5 flex-1" placeholder="Job code (optional)" value={handoverJobCode} onChange={e => setHandoverJobCode(e.target.value)} />
                        <button onClick={async () => {
                          if (!handoverContent.trim()) return
                          setSavingHandover(true)
                          try {
                            let jobId = ''
                            if (handoverJobCode.trim()) {
                              try {
                                const jobRes = await api.get(`/jobs?search=${handoverJobCode.trim()}&limit=1`)
                                jobId = jobRes.data.jobs?.[0]?.id || ''
                              } catch {}
                            }
                            await api.post('/handover', { content: handoverContent, priority: handoverPriority, jobId: jobId || undefined })
                            setHandoverContent('')
                            setHandoverJobCode('')
                            setHandoverPriority('normal')
                            setShowHandoverForm(false)
                            fetchHandoverNotes()
                          } catch {}
                          setSavingHandover(false)
                        }} disabled={savingHandover || !handoverContent.trim()}
                          className="btn-primary text-xs py-1.5 px-4 disabled:opacity-50">
                          {savingHandover ? 'Saving...' : 'Post'}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="divide-y divide-gray-50 dark:divide-[#1e3a5f]">
                    {handoverNotes.map(n => (
                      <div key={n.id} className={`px-5 py-3 flex items-start gap-3 ${n.priority === 'urgent' ? 'bg-red-50/50 dark:bg-red-900/5' : ''}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {n.priority === 'urgent' && <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-semibold uppercase">Urgent</span>}
                            <span className="text-xs font-semibold text-navy-800 dark:text-white">{n.authorName}</span>
                            <span className="text-[10px] text-gray-400">{new Date(n.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} · {new Date(n.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                            {n.jobCode && <span className="text-[10px] font-mono text-orange-500 bg-orange-50 dark:bg-orange-900/20 px-1.5 py-0.5 rounded">{n.jobCode}</span>}
                          </div>
                          <p className="text-sm text-navy-700 dark:text-gray-200">{n.content}</p>
                          {n.jobTitle && <p className="text-[10px] text-gray-400 mt-0.5 truncate">{n.jobTitle}</p>}
                        </div>
                        {(hasPermission('approve_output') || n.authorName === user?.name) && (
                          <button onClick={async () => {
                            await api.delete(`/handover/${n.id}`)
                            fetchHandoverNotes()
                          }} className="p-1 rounded text-gray-300 hover:text-red-400 transition-colors flex-shrink-0">
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end">
                <button onClick={() => { setShowHandover(true); setShowHandoverForm(true) }}
                  className="text-xs text-gray-400 hover:text-blue-500 flex items-center gap-1 transition-colors">
                  <MessageSquare size={12} /> Handover note
                </button>
              </div>
            </>
          )}

        </div>
      )}

      {canViewDashboard && (
        <>
          {isArtist && !canManage && (dailyTarget || (dashboardLeaderboardEnabled && hasAnyLeaderboardData)) && (
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
            {dailyTarget && <div className="card p-5 space-y-3 sm:w-[70%]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-display font-bold text-navy-900 dark:text-white">Today's Target</p>
                  <p className="text-xs text-gray-400">{dailyTarget.todayCredits} / {dailyTarget.dailyTarget} credits</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-navy-900 dark:text-white">{dailyTarget.percentage}%</p>
                  <div className="flex items-center gap-1 justify-end mt-0.5">
                    <Flame size={16} className="text-orange-500" />
                    <span className="text-sm font-semibold text-orange-500">{dailyTarget.currentStreak} day streak</span>
                  </div>
                </div>
              </div>
              <div className="w-full bg-gray-200 dark:bg-[#1a3050] rounded-full h-3 relative overflow-hidden">
                <div className={`h-3 rounded-full transition-all duration-500 ${
                  dailyTarget.percentage >= 100 ? 'bg-yellow-400' :
                  dailyTarget.percentage >= 80 ? 'bg-green-500' :
                  dailyTarget.percentage >= 50 ? 'bg-orange-400' : 'bg-red-400'
                }`} style={{ width: `${Math.min(100, dailyTarget.percentage)}%` }} />
                {dailyTarget.pendingPercentage > 0 && dailyTarget.percentage < 100 && (
                  <div className="h-3 absolute top-0 bg-gray-400 dark:bg-gray-500 opacity-60 rounded-r-full transition-all duration-500"
                    style={{
                      left: `${Math.min(100, dailyTarget.percentage)}%`,
                      width: `${Math.min(dailyTarget.pendingPercentage, 100 - dailyTarget.percentage)}%`
                    }} />
                )}
              </div>
              {dailyTarget.pendingCredits > 0 && (
                <p className="text-xs text-gray-400">+{dailyTarget.pendingCredits} credits pending QC approval</p>
              )}
              {dailyTarget.bestStreak > 0 && (
                <p className="text-xs text-gray-400">Best streak: <span className="font-semibold text-navy-700 dark:text-white">{dailyTarget.bestStreak} days</span></p>
              )}
            </div>}
            {dashboardLeaderboardEnabled && hasAnyLeaderboardData && (
            <div className="card overflow-hidden sm:w-[30%] cursor-pointer" onClick={() => navigate('/performance')}>
              <div className="px-5 py-3 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between">
                <p className="font-display font-semibold text-navy-900 dark:text-white flex items-center gap-2"><Trophy size={16} className="text-yellow-500" /> Leaderboard</p>
                <div className="flex gap-1 bg-gray-100 dark:bg-[#1a3050] rounded-xl p-1" onClick={e => e.stopPropagation()}>
                  {(['weekly', 'monthly'] as const).map(p => (
                    <button key={p} onClick={() => setLeaderboardPeriod(p)}
                      className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-colors capitalize ${leaderboardPeriod === p ? 'bg-white dark:bg-[#0d1e35] text-navy-800 dark:text-white shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div className="px-5 py-4">
                {myRank ? (
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-orange-50 dark:bg-orange-900/20 rounded-2xl flex flex-col items-center justify-center flex-shrink-0">
                      <span className="text-2xl font-bold text-orange-500">#{myRank.rank}</span>
                      <span className="text-xs text-orange-400">rank</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-navy-800 dark:text-white">{myRank.netCredits} credits {leaderboardPeriod === 'weekly' ? 'this week' : 'this month'}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{myRank.jobCount} jobs completed</p>
                      {myRank.currentStreak >= 1 && (
                        <p className="text-xs text-orange-500 mt-0.5 flex items-center gap-1"><Flame size={11} /> {myRank.currentStreak} day streak {myRank.bestStreak > myRank.currentStreak ? `· best: ${myRank.bestStreak}` : ''}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-2">No credits recorded yet this {leaderboardPeriod === 'weekly' ? 'week' : 'month'}</p>
                )}
                <p className="text-xs text-gray-400 text-center mt-3">Tap to see full leaderboard →</p>
              </div>
            </div>
            )}
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-4 mb-7">
            {/* Left 60% — info boxes */}
            <div className="sm:w-[70%] space-y-4">
              {hasPermission('view_team_energy') && (() => {
                    const total = commonStats.todayJobs
                    const pending = commonStats.pendingJobs
                    const inProgress = commonStats.inProgressJobs
                    const staffCount = adminStats.staffMembers || 1
                    const capacityPerStaff = 4
                    const teamCapacity = staffCount * capacityPerStaff
                    const activeLoad = pending + inProgress
                    const urgent = activeLoad > teamCapacity ? activeLoad - teamCapacity : 0
                    const loadScore = Math.min(100, Math.round((activeLoad / Math.max(1, teamCapacity)) * 100))
                    const energy = loadScore >= 75 ? { label: 'High Pressure', color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-900/10', bar: 'bg-red-400', border: 'border-red-100 dark:border-red-900/20', emoji: '🔴' }
                      : loadScore >= 45 ? { label: 'Busy', color: 'text-orange-500', bg: 'bg-orange-50 dark:bg-orange-900/10', bar: 'bg-orange-400', border: 'border-orange-100 dark:border-orange-900/20', emoji: '🟠' }
                      : loadScore >= 20 ? { label: 'Active', color: 'text-green-500', bg: 'bg-green-50 dark:bg-green-900/10', bar: 'bg-green-400', border: 'border-green-100 dark:border-green-900/20', emoji: '🟢' }
                      : { label: 'Light Day', color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/10', bar: 'bg-blue-400', border: 'border-blue-100 dark:border-blue-900/20', emoji: '🔵' }
                    return (
                      <div className={`rounded-2xl border ${energy.border} ${energy.bg} px-5 py-4 flex items-center justify-between gap-4`}>
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{energy.emoji}</span>
                          <div>
                            <p className={`text-sm font-bold ${energy.color}`}>Today: {energy.label}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {total} jobs today · {pending} pending · {inProgress} in progress
                              {urgent > 0 && <span className="text-red-500 font-medium"> · {urgent} jobs over capacity</span>}
                            </p>
                          </div>
                        </div>
                        <div className="flex-shrink-0 w-32">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] text-gray-400">Team Load</span>
                            <span className={`text-[10px] font-bold ${energy.color}`}>{loadScore}%</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-[#1a3050] rounded-full h-2">
                            <div className={`h-2 rounded-full transition-all duration-500 ${energy.bar}`} style={{ width: `${loadScore}%` }} />
                          </div>
                        </div>
                      </div>
                    )
                  })()}
              {isAdmin ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <StatCard label="Active Clients" value={adminStats.activeClients} color="bg-emerald-500" />
                  <StatCard label="Subscribed Clients" value={adminStats.subscriptionClients} color="bg-blue-500" />
                  <StatCard label="Staff Members" value={adminStats.staffMembers} color="bg-navy-700" />
                </div>
              ) : canViewDashboard && !hasPermission('access_jobs') && !hasPermission('can_work_on_jobs') ? (
                <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
                  <StatCard label="Staff Members" value={adminStats.staffMembers} color="bg-navy-700" />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <StatCard label="Today's Jobs" value={commonStats.todayJobs} color="bg-orange-500" />
                    <StatCard label="Overall Pending" value={commonStats.pendingJobs} color="bg-yellow-500" />
                    <StatCard label="Overall In Progress" value={commonStats.inProgressJobs} color="bg-blue-500" />
                  </div>
                </div>
              )}
          {(hasPermission('access_jobs') || hasPermission('can_work_on_jobs')) && <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {isArtist && !canManage ? (
              <>
                <StatCard label="My Jobs Today" value={myStats.myJobs} color="bg-navy-700" />
                <StatCard label="My Active Jobs" value={myStats.myInProgress} color="bg-purple-500" />
                <StatCard label="My Done Today" value={myStats.myCompleted} color="bg-green-500" />
              </>
            ) : (
              <>
                <StatCard label="Today's Volume" value={myStats.myJobs} color="bg-orange-500" />
                <StatCard label="Active Today" value={myStats.myInProgress} color="bg-purple-500" />
                <StatCard label="Completed Today" value={myStats.myCompleted} color="bg-green-500" />
              </>
            )}
          </div>}
            </div>
            {/* Right 40% — Today & This Week */}
            {birthdayWallEnabled && (
              <div className="sm:w-[30%] card overflow-hidden self-start">
                <div className="px-5 py-3.5 border-b border-gray-100 dark:border-[#1e3a5f]">
                  <p className="font-display font-semibold text-navy-900 dark:text-white flex items-center gap-2"><CalendarDays size={16} className="text-orange-500" /> Today & This Week</p>
                </div>
                {birthdays.length === 0 ? (
                  <div className="px-5 py-6 flex flex-col items-center justify-center text-center gap-2">
                    <CalendarDays size={28} className="text-gray-200 dark:text-gray-600" />
                    <p className="text-sm font-medium text-gray-400">All clear this week</p>
                    <p className="text-xs text-gray-300 dark:text-gray-600">No birthdays or anniversaries in the next 7 days</p>
                  </div>
                ) : (
                <div className="divide-y divide-gray-50 dark:divide-[#1e3a5f]">
                  {birthdays.map(b => (
                    <div key={b.userId} className={`px-4 py-3 flex items-center gap-3 ${b.isToday ? 'bg-yellow-50 dark:bg-yellow-900/10' : ''}`}>
                      <span className="flex-shrink-0">{b.type === 'anniversary' ? (b.isToday ? <Gift size={18} className="text-purple-500" /> : <CalendarDays size={18} className="text-indigo-400" />) : (b.isToday ? <Cake size={18} className="text-pink-400" /> : <Calendar size={18} className="text-orange-400" />)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-navy-800 dark:text-white truncate">{b.name}</p>
                        <p className="text-xs text-gray-400">
                          {b.type === 'anniversary'
                            ? b.isToday
                              ? `${b.years} year${b.years !== 1 ? 's' : ''} work anniversary today!`
                              : `${b.years} yr anniversary · ${b.daysUntil === 1 ? 'Tomorrow' : `in ${b.daysUntil} days`} · ${(() => { const [d,m] = b.date.split('/'); return new Date(2000, parseInt(m)-1, parseInt(d)).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) })()}`
                            : b.isToday
                              ? 'Birthday today!'
                              : `Birthday · ${b.daysUntil === 1 ? 'Tomorrow' : `in ${b.daysUntil} days`} · ${(() => { const [d,m] = b.date.split('/'); return new Date(2000, parseInt(m)-1, parseInt(d)).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) })()}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Bottom section */}
      {isArtist && hasPermission('access_jobs') ? (
        <div className="card overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-navy-900">My Jobs Today</h2>
          </div>
          {todayJobs.length === 0 ? (
            <div className="py-16 text-center text-gray-400 text-sm">No jobs today</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50/70">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Job Code</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Title</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {todayJobs.map(job => (
                  <tr key={job.id} onClick={() => navigate(`/jobs?openJob=${job.id}`)}
                    className="hover:bg-orange-50/30 transition-colors cursor-pointer">
                    <td className="px-6 py-3.5 font-mono text-xs text-navy-600 font-medium">{job.jobCode}</td>
                    <td className="px-6 py-3.5 text-navy-800 font-medium">{job.displayTitle}</td>
                    <td className="px-6 py-3.5">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[job.status] || 'bg-gray-100 text-gray-500'}`}>
                        {job.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (canViewAnalytics || canViewClientFlow) ? (
        <div className="space-y-4">
          {canViewClientFlow && clientFlow && (
            <div className="card p-5">
              <div className="flex items-center justify-between mb-4">
                <p className="font-display font-semibold text-navy-900 dark:text-white">Client Jobs Flow — Last 30 Days</p>
                <button onClick={() => setClientFlowChart(v => v === 'bar' ? 'line' : 'bar')}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-orange-500 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 transition-colors">
                  {clientFlowChart === 'bar' ? <TrendingUp size={12} /> : <BarChart2 size={12} />}
                  {clientFlowChart === 'bar' ? 'Line' : 'Bar'}
                </button>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                {clientFlowChart === 'bar' ? (
                  <BarChart data={clientFlow.data} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} interval={4} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {clientFlow.clients.map((client: string, i: number) => (
                      <Bar key={client} dataKey={client} stackId="a"
                        fill={['#f97316','#3b82f6','#10b981','#8b5cf6','#f59e0b','#ef4444','#06b6d4','#84cc16'][i % 8]}
                        radius={i === clientFlow.clients.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
                    ))}
                  </BarChart>
                ) : (
                  <LineChart data={clientFlow.data} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} interval={4} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {clientFlow.clients.map((client: string, i: number) => (
                      <Line key={client} dataKey={client} type="monotone"
                        stroke={['#f97316','#3b82f6','#10b981','#8b5cf6','#f59e0b','#ef4444','#06b6d4','#84cc16'][i % 8]}
                        strokeWidth={2} dot={false} />
                    ))}
                  </LineChart>
                )}
              </ResponsiveContainer>
            </div>
          )}
          {canViewAnalytics && analytics && (<>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="card p-5">
              <div className="flex items-center justify-between mb-4">
                <p className="font-display font-semibold text-navy-900 dark:text-white">Jobs — Last 30 Days</p>
                <button onClick={() => setJobsChart(v => v === 'bar' ? 'line' : 'bar')}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-orange-500 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 transition-colors">
                  {jobsChart === 'bar' ? <TrendingUp size={12} /> : <BarChart2 size={12} />}
                  {jobsChart === 'bar' ? 'Line' : 'Bar'}
                </button>
              </div>
              <ResponsiveContainer width="100%" height={180}>
                {jobsChart === 'bar' ? (
                  <BarChart data={analytics.byDay} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} interval={4} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="fresh" stackId="a" fill="#f97316" name="Fresh" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="revision" stackId="a" fill="#3b82f6" name="Revision" radius={[3, 3, 0, 0]} />
                  </BarChart>
                ) : (
                  <LineChart data={analytics.byDay} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} interval={4} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line dataKey="fresh" type="monotone" stroke="#f97316" strokeWidth={2} dot={false} name="Fresh" />
                    <Line dataKey="revision" type="monotone" stroke="#3b82f6" strokeWidth={2} dot={false} name="Revision" />
                  </LineChart>
                )}
              </ResponsiveContainer>
            </div>
            {analytics.byHours && (
              <div className="card p-5">
                <div className="flex items-center justify-between mb-4">
                  <p className="font-display font-semibold text-navy-900 dark:text-white">Work Hours — Last 30 Days</p>
                  <button onClick={() => setHoursChart(v => v === 'bar' ? 'line' : 'bar')}
                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-orange-500 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 transition-colors">
                    {hoursChart === 'bar' ? <TrendingUp size={12} /> : <BarChart2 size={12} />}
                    {hoursChart === 'bar' ? 'Line' : 'Bar'}
                  </button>
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  {hoursChart === 'bar' ? (
                    <BarChart data={analytics.byHours} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} interval={4} />
                      <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} formatter={(v: any) => [`${v} hrs`, 'Hours']} />
                      <Bar dataKey="hours" fill="#6366f1" radius={[3, 3, 0, 0]} name="Hours" />
                    </BarChart>
                  ) : (
                    <LineChart data={analytics.byHours} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} interval={4} />
                      <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} formatter={(v: any) => [`${v} hrs`, 'Hours']} />
                      <Line dataKey="hours" type="monotone" stroke="#6366f1" strokeWidth={2} dot={false} name="Hours" />
                    </LineChart>
                  )}
                </ResponsiveContainer>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="card p-5">
                <p className="font-display font-semibold text-navy-900 dark:text-white mb-4">By Status <span className="text-xs font-normal text-gray-400">— last 30 days</span></p>
                <div className="space-y-2">
                  {analytics.byStatus.sort((a: any, b: any) => b.count - a.count).map((s: any) => (
                    <div key={s.status} className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-24 truncate flex-shrink-0">{s.status}</span>
                      <div className="flex-1 h-2 bg-gray-100 dark:bg-[#1a3050] rounded-full overflow-hidden">
                        <div className="h-full bg-orange-400 rounded-full"
                          style={{ width: `${Math.round((s.count / analytics.byStatus.reduce((a: any, b: any) => a + b.count, 0)) * 100)}%` }} />
                      </div>
                      <span className="text-xs font-semibold text-navy-800 dark:text-white w-6 text-right">{s.count}</span>
                    </div>
                  ))}
                </div>
              </div>
              {analytics.byClient?.length > 0 && (
                <div className="card p-5">
                  <p className="font-display font-semibold text-navy-900 dark:text-white mb-4">Client Activity <span className="text-xs font-normal text-gray-400">— last 30 days</span></p>
                  <div className="space-y-2">
                    {analytics.byClient.map((c: any) => {
                      const max = analytics.byClient[0].count
                      return (
                        <div key={c.name} className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-20 truncate flex-shrink-0">{c.name}</span>
                          <div className="flex-1 h-2 bg-gray-100 dark:bg-[#1a3050] rounded-full overflow-hidden">
                            <div className="h-full bg-blue-400 rounded-full transition-all"
                              style={{ width: `${Math.round((c.count / max) * 100)}%` }} />
                          </div>
                          <span className="text-xs font-semibold text-navy-800 dark:text-white w-6 text-right">{c.count}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-4">
              <div className="card p-5">
                <p className="font-display font-semibold text-navy-900 dark:text-white mb-3">By Type (30 days)</p>
                <div className="flex items-center justify-around">
                  {analytics.byType.map((t: any) => (
                    <div key={t.name} className="text-center">
                      <p className="text-2xl font-bold text-navy-900 dark:text-white">{t.value}</p>
                      <p className="text-xs text-gray-400">{t.name}</p>
                      {t.hours > 0 && <p className="text-xs text-indigo-500 font-medium mt-0.5">{t.hours}h</p>}
                      {t.spark && <Sparkline data={t.spark} color={t.name === 'Fresh' ? '#f97316' : t.name === 'Revision' ? '#3b82f6' : '#ef4444'} />}
                    </div>
                  ))}
                </div>
              </div>
              {analytics.byArtist.length > 0 && (
                <div className="card p-5">
                  <p className="font-display font-semibold text-navy-900 dark:text-white mb-3">Top Artists This Month <span className="text-xs font-normal text-gray-400">(by credits)</span></p>
                  <div className="space-y-2">
                    {analytics.byArtist.map((a: any, i: number) => (
                      <div key={a.name} className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 w-4">{i + 1}</span>
                        <span className="text-xs text-navy-700 dark:text-gray-300 flex-1">{a.name}</span>
                        <span className="text-xs font-semibold text-orange-500">{a.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          </>)}
        </div>
      ) : (
        <div className="card p-16 text-center text-gray-400 text-sm">Loading analytics...</div>
      )}

      {/* My Schedule — visible to staff with can_work_on_jobs */}
      {hasPermission('can_work_on_jobs') && <div className="mt-6"><MyScheduleCard /></div>}

      {/* Unread Notice Popup */}
      {currentUnread && createPortal(
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#0d1e35] rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3.5 flex items-center justify-between border-b border-gray-100 dark:border-[#1e3a5f]">
              <div className="flex items-center gap-2">
                <Pin size={14} className="text-orange-400" />
                <p className="font-semibold text-sm text-navy-900 dark:text-white">Ink Bulletin</p>
                {unreadNotices.length > 1 && (
                  <span className="bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unreadNotices.length}</span>
                )}
              </div>
              <button onClick={async () => {
                await api.post(`/notices/${currentUnread.id}/read`).catch(() => {})
                const remaining = unreadNotices.filter(n => n.id !== currentUnread.id)
                setUnreadNotices(remaining)
                setCurrentUnread(remaining.length > 0 ? remaining[0] : null)
              }} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X size={16} />
              </button>
            </div>
            <div className="p-6">
              {currentUnread.isPinned && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-500 bg-orange-50 dark:bg-orange-900/20 px-2 py-0.5 rounded-full mb-3">
                  <Pin size={10} /> Pinned
                </span>
              )}
              <p className="text-lg font-bold text-navy-900 dark:text-white mb-2">{currentUnread.title}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{currentUnread.content}</p>
              {currentUnread.linkUrl && <button onClick={() => { const isInternal = currentUnread.linkUrl.includes(window.location.hostname) || currentUnread.linkUrl.startsWith('/'); isInternal ? navigate(currentUnread.linkUrl.replace(window.location.origin, '')) : window.open(currentUnread.linkUrl, '_blank') }} className="inline-flex items-center gap-1 text-sm text-orange-500 hover:text-orange-600 font-medium mt-3">{currentUnread.linkLabel || 'View details'} →</button>}
              <p className="text-xs text-gray-400 mt-4">{currentUnread.createdBy} · {new Date(currentUnread.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
            </div>
            <div className="px-6 pb-5 flex items-center justify-between">
              {unreadNotices.length > 1 ? (
                <p className="text-xs text-gray-400">{unreadNotices.length - 1} more notice{unreadNotices.length - 1 > 1 ? 's' : ''} after this</p>
              ) : <div />}
              <button onClick={async () => {
                await api.post(`/notices/${currentUnread.id}/read`).catch(() => {})
                const remaining = unreadNotices.filter(n => n.id !== currentUnread.id)
                setUnreadNotices(remaining)
                setCurrentUnread(remaining.length > 0 ? remaining[0] : null)
              }} className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium px-5 py-2 rounded-xl transition-colors">
                {unreadNotices.length > 1 ? 'Next' : 'Got it'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      </div>
  )
}

function MyScheduleCard() {
  const [shifts, setShifts] = useState<Record<string, string>>({})
  const [weekStart, setWeekStart] = useState('')
  const [loading, setLoading] = useState(true)
  const [currentWeek, setCurrentWeek] = useState<Date>((() => {
    const d = new Date()
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1)
    return new Date(d.setDate(diff))
  })())

  const fetchShifts = (weekDate: Date) => {
    setLoading(true)
    const weekStr = weekDate.toISOString().split('T')[0]
    api.get(`/shifts/my?weekStart=${weekStr}`).then(res => {
      setShifts(res.data.shifts || {})
      setWeekStart(res.data.weekStart || weekStr)
    }).catch(() => {}).finally(() => setLoading(false))
  }

  useEffect(() => { fetchShifts(currentWeek) }, [])

  const goToPrevWeek = () => {
    const prev = new Date(currentWeek)
    prev.setDate(prev.getDate() - 7)
    setCurrentWeek(prev)
    fetchShifts(prev)
  }

  const goToNextWeek = () => {
    const next = new Date(currentWeek)
    next.setDate(next.getDate() + 7)
    setCurrentWeek(next)
    fetchShifts(next)
  }

  const isCurrentWeek = () => {
    const today = new Date()
    const day = today.getDay()
    const diff = today.getDate() - day + (day === 0 ? -6 : 1)
    const thisMonday = new Date(new Date().setDate(diff))
    return currentWeek.toDateString() === thisMonday.toDateString()
  }

  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const shiftColors: Record<string, string> = {
    day: 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400',
    night: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400',
    off: 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400',
  }
  const shiftIcons: Record<string, any> = { 
    day: <Sun size={14} className="text-orange-500" />, 
    night: <Moon size={14} className="text-indigo-400" />, 
    off: <XCircle size={14} className="text-red-400" /> 
  }
  const shiftLabels: Record<string, string> = { day: 'Day', night: 'Night', off: 'Off' }

  const dates = weekStart ? Array.from({ length: 6 }, (_, i) => {
    const d = new Date(weekStart)
    d.setDate(d.getDate() + i)
    return d.toISOString().split('T')[0]
  }) : []

  const weekEnd = weekStart ? new Date(weekStart) : null
  if (weekEnd) weekEnd.setDate(weekEnd.getDate() + 5)
  const weekLabel = weekStart ? `${new Date(weekStart).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} — ${weekEnd?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : ''

  if (loading) return (
    <div className="card p-6">
      <div className="skeleton h-5 w-40 mb-4" />
      <div className="grid grid-cols-6 gap-2">
        {[...Array(6)].map((_, i) => <div key={i} className="skeleton h-16 rounded-xl" />)}
      </div>
    </div>
  )

  if (!dates.length) return null

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-semibold text-navy-900 dark:text-white flex items-center gap-2"><Calendar size={16} className="text-orange-500" /> My Schedule</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{weekLabel}</span>
          <div className="flex items-center gap-1">
            <button onClick={goToPrevWeek} className="w-6 h-6 rounded-lg bg-gray-100 dark:bg-[#112240] hover:bg-orange-100 dark:hover:bg-orange-900/20 flex items-center justify-center text-gray-500 hover:text-orange-500 transition-colors">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            {!isCurrentWeek() && (
              <button onClick={() => { const now = new Date(); const day = now.getDay(); const diff = now.getDate() - day + (day === 0 ? -6 : 1); const monday = new Date(new Date().setDate(diff)); setCurrentWeek(monday); fetchShifts(monday) }}
                className="text-[10px] text-orange-500 hover:text-orange-600 font-medium px-1.5 py-0.5 rounded bg-orange-50 dark:bg-orange-900/20 transition-colors">
                Today
              </button>
            )}
            <button onClick={goToNextWeek} className="w-6 h-6 rounded-lg bg-gray-100 dark:bg-[#112240] hover:bg-orange-100 dark:hover:bg-orange-900/20 flex items-center justify-center text-gray-500 hover:text-orange-500 transition-colors">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-6 gap-2">
        {dates.map((date, i) => {
          const shift = shifts[date]
          if (!shift) return (
            <div key={date} className="flex flex-col items-center gap-1 p-2 rounded-xl bg-gray-50 dark:bg-[#112240] opacity-40">
              <p className="text-[10px] font-semibold text-gray-400 uppercase">{days[i]}</p>
              <p className="text-[10px] text-gray-300">{new Date(date).getDate()}</p>
              <span className="text-xs">—</span>
            </div>
          )
          return (
            <div key={date} className={`flex flex-col items-center gap-1 p-2 rounded-xl ${shiftColors[shift]}`}>
              <p className="text-[10px] font-semibold uppercase">{days[i]}</p>
              <p className="text-[10px] opacity-70">{new Date(date).getDate()}</p>
              <span className="flex items-center justify-center">{shiftIcons[shift]}</span>
              <p className="text-[9px] font-medium">{shiftLabels[shift]}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  const count = useCountUp(value)
  return (
    <div className="card p-5 transition-all duration-200 hover:shadow-[0_4px_16px_rgba(249,115,22,0.15)] hover:border-orange-200 group">
      <div className="flex items-baseline gap-2">
        <p className="text-4xl font-display font-bold text-[#123D68] dark:text-white leading-none group-hover:text-orange-500 transition-colors">{count}</p>
        <p className="text-xs font-light text-gray-400 leading-none">{label}</p>
      </div>
    </div>
  )
}

export default function DashboardPage() {
  const { user, hasPermission } = useAuth()
  if (!user) return null
  return user.roleName === 'client' ? <ClientDashboard user={user} /> : <StaffDashboard user={user} />
}